import { EnterHook, RouterState } from "react-router";
import { Action, Store } from "redux";
import { handleError } from "../../errorHandler";
import { getQueryStringValues } from "@travellocal/utils/urlHelpers";
import { SharedActions } from "./sharedActions";
import { CustomErrors } from "../../errors";
import { isBrowser } from "../../../core/detection";

export interface IEnterHookPromise<T> {
  factory: (nextState?: RouterState, state?: T) => Action | Action[];
}

export function createEnterHookPromise<T>(factory: (nextState?: RouterState, state?: T) => Action | Action[]): IEnterHookPromise<T> {
  return {
    factory,
  };
}

export function apiPromiseHook<T>(...apiPromises: IEnterHookPromise<T>[]) {
  return (store: Store<T>, serverRender: boolean): EnterHook => {
    return apiPromiseEnterHook(store, serverRender, apiPromises);
  };
}

function apiPromiseEnterHook(store: Store<any>, serverRender: boolean, apiPromises: IEnterHookPromise<any>[]): EnterHook {
  return async (nextState, replaceState, callback) => {

    // Populate location.query
    if (!nextState.location.query && nextState.location.search) {
      nextState.location.query = getQueryStringValues(nextState.location.search);
    }

    try {
      const storeState = store.getState();

      // Enterhooks can either return actions or an array of actions
      // Flatten and start them all
      const storeActions: Action[] = [];
      for (const apiPromise of apiPromises) {
        const action = apiPromise.factory(nextState, storeState);
        if (Array.isArray(action)) {
          for (const a of action) {
            storeActions.push(a);
          }
        }
        else if (action != null) {
          storeActions.push(action);
        }
      }

      if (storeActions.length > 0) {
        await Promise.all(storeActions.map(a => store.dispatch(a)));
      }
      callback();
    }
    catch (e) {
      // don't pass error to match callback if error occurs on client side
      if (isBrowser() || (e instanceof CustomErrors.ApiError && e.response.status === 404)) {
        if (e.response?.isPrimary) {
          nextState.location.state = { notFound: true };
        }
        callback();
      }
      else {
        callback(e);
      }

      handleError(e);
    }
  };
}
